import { useAdminContent, useFeatureFlags } from "context/admin-content";
import { useRiskProfile } from "context/risk-profile";
import { usePatient } from "context/advert/use-patient";
import { PatientResults } from "context/admin-content/types";

type Props = {
  riskResult: PatientResults | undefined;
};

export const useTakeAction = ({ riskResult }: Props) => {
  const featureFlags = useFeatureFlags();
  const riskProfile = useRiskProfile();
  const { patient, refetch: refetchPatient, isLoading } = usePatient();
  const adminData = useAdminContent();
  const appointmentsInterest = patient?.screening_appointments_interest;
  const appointmentsAvailability =
    patient?.preferred_appointment_time?.availability;

  const isTestingGuidance =
    riskProfile?.riskProfile?.attributes?.testingGuidance?.meets_criteria ===
      true && adminData?.patientResults?.testing?.showScheduler;

  const isMriGuidance =
    featureFlags.enableBreastMRIWorkflow &&
    riskProfile?.riskProfile?.attributes?.mriGuidance?.meets_criteria ===
      true &&
    adminData?.patientResults?.breastMri?.showScheduler;

  const isLungCTGuidance =
    featureFlags.enableLungCTWorkflow &&
    riskProfile?.riskProfile?.attributes?.lungGuidance?.meets_criteria ===
      true &&
    adminData?.patientResults?.lungCt?.showScheduler;

  const breastMriDisabledButEligible =
    !featureFlags.enableBreastMRIWorkflow &&
    riskProfile?.riskProfile?.attributes?.mriGuidance?.meets_criteria === true;

  const geneticTestingCheckboxEnabled =
    (!appointmentsInterest?.testing && isTestingGuidance) ||
    breastMriDisabledButEligible;

  const breastMRICheckboxEnabled = !appointmentsInterest?.mri && isMriGuidance;

  const lungCTCheckboxEnabled = !appointmentsInterest?.lung && isLungCTGuidance;

  const isGeneticTestingDeclinedOrNotSet =
    !appointmentsInterest?.testing ||
    appointmentsInterest?.testing?.level === "DECLINED";

  const showGeneticTesting =
    isGeneticTestingDeclinedOrNotSet && isTestingGuidance;

  const showGeneticTestingAccordion =
    breastMriDisabledButEligible ||
    (isGeneticTestingDeclinedOrNotSet && isTestingGuidance);

  const showBreastMRI =
    (!appointmentsInterest?.mri ||
      appointmentsInterest?.mri?.level === "DECLINED") &&
    isMriGuidance;

  const showLungCT =
    (!appointmentsInterest?.lung ||
      appointmentsInterest?.lung?.level === "DECLINED") &&
    isLungCTGuidance;

  return {
    refetchPatient,
    appointmentsAvailability,
    showBreastMRI: Boolean(showBreastMRI),
    showGeneticTesting: Boolean(showGeneticTestingAccordion),
    showLungCT: Boolean(showLungCT),
    isLoading: Boolean(riskProfile?.isLoading || isLoading),
    breastMRICheckboxEnabled: Boolean(breastMRICheckboxEnabled),
    geneticTestingCheckboxEnabled: Boolean(geneticTestingCheckboxEnabled),
    lungCTCheckboxEnabled: Boolean(lungCTCheckboxEnabled),
    showScheduler: Boolean(
      riskResult?.showScheduler &&
        (showGeneticTesting || showBreastMRI || showLungCT)
    ),
  };
};
